import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useCurrentUser from "hooks/useCurrentUser";
import axios from "../../api/axios";
import { toast } from "react-toastify";
import {
  PageContainer, PremiumCard, Button, LoadingState, EmptyState,
} from "components/ui";

const SubscriptionPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const { user, loading: userLoading } = useCurrentUser();
  const tenant = user?.tenant;

  const planId = location.state?.planId;
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState("MONTHLY");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!userLoading && !tenant) navigate("/tenant/dashboard");
    if (!planId) navigate("/home");
  }, [userLoading, tenant, planId]);

  useEffect(() => {
    if (!planId) return;
    (async () => {
      try {
        const res = await axios.get(`/api/plans/${planId}`);
        setPlan(res.data);
      } catch (_) {
        toast.error("Plan not found");
        navigate("/home");
      } finally {
        setPlanLoading(false);
      }
    })();
  }, [planId]);

  const handleSubscribe = async () => {
    setSubmitting(true);
    try {
      const res = await axiosPrivate.post("/api/subscriptions", { planId, billingCycle });
      if (res.data.trial) {
        toast.success("3-day free trial started! Enjoy full access.");
        navigate("/tenant/dashboard");
      } else {
        window.location.href = res.data.paymentUrl;
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Subscription failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (userLoading || planLoading) {
    return (
      <PageContainer narrow>
        <PremiumCard><LoadingState /></PremiumCard>
      </PageContainer>
    );
  }
  if (!plan) {
    return (
      <PageContainer narrow>
        <PremiumCard>
          <EmptyState
            title="Plan not available"
            action={<Button onClick={() => navigate("/home")}>Browse plans</Button>}
          />
        </PremiumCard>
      </PageContainer>
    );
  }

  const price = billingCycle === "MONTHLY" ? plan.monthlyPrice : plan.yearlyPrice;

  return (
    <PageContainer narrow>
      <PremiumCard title={`${plan.name} plan`} subtitle={plan.description}>
        <div style={{ marginBottom: 24 }}>
          <label className="t-body-xs" style={{ marginBottom: 10, display: "block" }}>Billing cycle</label>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            <CycleButton
              active={billingCycle === "MONTHLY"}
              onClick={() => setBillingCycle("MONTHLY")}
              label="Monthly"
              price={`${plan.monthlyPrice} ${plan.currency}`}
              sub="per month"
            />
            <CycleButton
              active={billingCycle === "YEARLY"}
              onClick={() => setBillingCycle("YEARLY")}
              label="Yearly"
              price={`${plan.yearlyPrice} ${plan.currency}`}
              sub="per year"
              badge="Best value"
            />
          </div>
        </div>

        <div
          style={{
            background: "var(--surface-muted)",
            border: "1px solid var(--border-subtle)",
            borderRadius: 12,
            padding: 16,
            display: "grid", gap: 10,
            marginBottom: 24,
          }}
        >
          <Line label="Plan" value={plan.name} />
          <Line label="Billing" value={billingCycle === "MONTHLY" ? "Monthly" : "Yearly"} />
          <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 10, marginTop: 4 }}>
            <Line label="Total" value={<strong className="t-accent t-mono">{price} {plan.currency}</strong>} emphasized />
          </div>
        </div>

        <Button variant="primary" size="lg" block loading={submitting} onClick={handleSubscribe}>
          Proceed to payment
        </Button>
        <p className="t-body-sm t-muted" style={{ textAlign: "center", marginTop: 12 }}>
          You can cancel anytime · 3-day free trial available
        </p>
      </PremiumCard>
    </PageContainer>
  );
};

const Line = ({ label, value, emphasized }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
    <span className={emphasized ? "" : "t-muted"}>{label}</span>
    <span style={emphasized ? { fontWeight: 600 } : undefined}>{value}</span>
  </div>
);

const CycleButton = ({ active, onClick, label, price, sub, badge }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      position: "relative",
      padding: "16px 18px",
      borderRadius: 12,
      border: `1px solid ${active ? "var(--brand-primary)" : "var(--border-default)"}`,
      background: active ? "var(--brand-primary-50)" : "var(--surface)",
      cursor: "pointer",
      textAlign: "left",
      transition: "all var(--duration-base) var(--ease)",
      boxShadow: active ? "var(--shadow-ring)" : "none",
    }}
  >
    {badge && (
      <span
        style={{
          position: "absolute", top: -10, right: 14,
          background: "var(--brand-primary)", color: "#fff",
          fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
          padding: "3px 10px", borderRadius: 999, textTransform: "uppercase",
        }}
      >
        {badge}
      </span>
    )}
    <div className="t-body-xs" style={{ marginBottom: 4 }}>{label}</div>
    <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 22, color: "var(--text-primary)", lineHeight: 1 }}>
      {price}
    </div>
    <div className="t-body-sm t-muted" style={{ marginTop: 4 }}>{sub}</div>
  </button>
);

export default SubscriptionPage;
